// ─── Constants ────────────────────────────────────────────────────────────────

const LOCAL_STORAGE_WISHLIST_KEY = 'shopify-wishlist';
const LOCAL_STORAGE_WISHLIST_EMAIL_KEY = 'shopify-wishlist-email';
const LOCAL_STORAGE_DELIMITER = ',';
const BUTTON_ACTIVE_CLASS = 'active';
const GRID_LOADED_CLASS = 'loaded';

const CARTSEE_URL = 'https://tss.jialenet.com/api/v1/cartsee/event';
const CARTSEE_WISHLIST_FORM_ID = String(
  window.__wishlistCartseeConfig?.formId || ''
).trim();
const CARTSEE_WISHLIST_FORM_SELECTOR = CARTSEE_WISHLIST_FORM_ID
  ? `[data-form-id="${window.CSS?.escape?.(CARTSEE_WISHLIST_FORM_ID) || CARTSEE_WISHLIST_FORM_ID.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"]`
  : '';

const selectors = {
  button: '[button-wishlist]',
  grid: '[grid-wishlist]',
  productBlock: '.product-block',
  promotionCard: '.promotion-wrapper',
};

// 获取 cartsee customer 信息（从 window.__cartseeCustomer 注入）
const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(email));

const getStoredWishlistEmail = () => {
  try {
    return normalizeEmail(localStorage.getItem(LOCAL_STORAGE_WISHLIST_EMAIL_KEY));
  } catch (err) {
    return '';
  }
};

const storeWishlistEmail = (email) => {
  const normalizedEmail = normalizeEmail(email);
  if (!isValidEmail(normalizedEmail)) return '';

  try {
    localStorage.setItem(LOCAL_STORAGE_WISHLIST_EMAIL_KEY, normalizedEmail);
  } catch (err) {
    console.warn('[Wishlist] Unable to cache guest email:', err);
  }

  return normalizedEmail;
};

const getCartseeCustomer = function() {
  const c = (window.__cartseeCustomer && window.__cartseeCustomer()) || {};
  return {
    id: c.id || '',
    email: normalizeEmail(c.email) || getStoredWishlistEmail(),
    phone: c.phone || '', 
  };
};

// 生成事件唯一 ID
const generateEventId = function(actionType, productId) {
  return 'evt_' + Date.now() + '_' + actionType + '_' + (productId || 'no_product');
};

// 调用 cartsee API
const sendCartseeEvent = function(actionType, productId, productTitle) {
  const customer = getCartseeCustomer();
  // id/email/phone 至少有一个
  if (!customer.id && !customer.email && !customer.phone) return;

  const eventId = generateEventId(actionType, productId);
  const eventTime = new Date().toISOString().slice(0, 19).replace('T', ' ') + '-00:00';

  const properties = [
    { attr_id: "wishlist_url", attr_value: "https://www.fioboc.com/pages/wishlist" },
    { attr_id: "action_type", attr_value: actionType },
  ];
  if (productId) {
    properties.push({ attr_id: "product_id", attr_value: String(productId) });
  }
  if (productTitle) {
    properties.push({ attr_id: "product_name", attr_value: productTitle });
  }

  const requestBody = {
    customer: customer,
    event_name: 'shopify-wishlist:updated',
    event_time: eventTime,
    id: eventId,
  };

  if (productId) {
    requestBody.product_id = [String(productId)];
  }

  if (properties.length > 0) {
    requestBody.properties = properties;
  }

  // 调用 cartsee API
  fetch(CARTSEE_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(requestBody),
  }).then(function(res) {
    console.log('[Wishlist] Cartsee event sent:', res.status);
  }).catch(function(err) {
    console.warn('[Wishlist] Cartsee event failed:', err);
  });
};

let pendingWishlistAdd = null;
let cartseeFormObserver = null;
let cartseeFormWatchTimer = null;
let cartseeFormOpenTimers = [];
let cartseeSubscribeCallbackInitialized = false;

const clearCartseeFormOpenTimers = () => {
  cartseeFormOpenTimers.forEach((timer) => window.clearTimeout(timer));
  cartseeFormOpenTimers = [];
};

const clearPendingWishlistAdd = () => {
  pendingWishlistAdd = null;
  cartseeFormObserver?.disconnect();
  cartseeFormObserver = null;
  window.clearTimeout(cartseeFormWatchTimer);
  cartseeFormWatchTimer = null;
  clearCartseeFormOpenTimers();
};

const isVisibleElement = (element) => Boolean(element?.getClientRects().length);

const getVisibleCartseeWishlistRoots = () => {
  if (!CARTSEE_WISHLIST_FORM_SELECTOR) return [];

  return Array.from(document.querySelectorAll(CARTSEE_WISHLIST_FORM_SELECTOR)).filter(isVisibleElement);
};

const hasVisibleWishlistCartseeForm = () => getVisibleCartseeWishlistRoots().length > 0;

const guestNeedsWishlistEmail = () => {
  const customer = getCartseeCustomer();
  return !customer.id && !customer.email && !customer.phone;
};

const completePendingWishlistAdd = (email) => {
  if (!pendingWishlistAdd) return;

  const storedEmail = storeWishlistEmail(email);
  if (!storedEmail) return;

  const pending = pendingWishlistAdd;
  clearPendingWishlistAdd();

  if (getWishlist().includes(pending.handle)) {
    sendCartseeEvent('wishlist_add', pending.productId, pending.productTitle);
  }
};

const installCartseeSubscribeCallback = () => {
  if (cartseeSubscribeCallbackInitialized) return;
  cartseeSubscribeCallbackInitialized = true;

  const existingSubscribe = window.CARTSEE_SUBSCRIBE || {};
  const previousOnSubscribed = typeof existingSubscribe.onSubscribed === 'function'
    ? existingSubscribe.onSubscribed
    : null;

  window.CARTSEE_SUBSCRIBE = {
    ...existingSubscribe,
    onSubscribed(data) {
      previousOnSubscribed?.call(this, data);

      if (!pendingWishlistAdd) return;
      if (data?.type && data.type !== 'email') return;

      const email = normalizeEmail(data?.email || data?.loginEmail);
      if (!isValidEmail(email)) return;

      completePendingWishlistAdd(email);
    },
  };
};

const watchCartseeWishlistForm = () => {
  cartseeFormObserver?.disconnect();
  window.clearTimeout(cartseeFormWatchTimer);

  const stopRetryIfFormVisible = () => {
    if (hasVisibleWishlistCartseeForm()) {
      cartseeFormObserver?.disconnect();
      cartseeFormObserver = null;
      window.clearTimeout(cartseeFormWatchTimer);
      cartseeFormWatchTimer = null;
      clearCartseeFormOpenTimers();
    }
  };

  cartseeFormObserver = new MutationObserver(stopRetryIfFormVisible);
  cartseeFormObserver.observe(document.documentElement, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'style', 'hidden'],
  });

  cartseeFormWatchTimer = window.setTimeout(() => {
    clearPendingWishlistAdd();
    console.warn('[Wishlist] Cartsee form was not shown. Wishlist item was still saved.');
  }, 5000);

  stopRetryIfFormVisible();
};

const openCartseeWishlistForm = (wishlistItem) => {
  if (!CARTSEE_WISHLIST_FORM_ID) return;

  pendingWishlistAdd = wishlistItem;
  installCartseeSubscribeCallback();
  watchCartseeWishlistForm();

  window.cartsee = window.cartsee || [];

  const requestOpen = () => {
    if (!pendingWishlistAdd) return;
    if (hasVisibleWishlistCartseeForm()) {
      clearCartseeFormOpenTimers();
      return;
    }

    window.cartsee.push(['openForm', CARTSEE_WISHLIST_FORM_ID]);
  };

  requestOpen();
  cartseeFormOpenTimers = [
    window.setTimeout(requestOpen, 600),
    window.setTimeout(requestOpen, 1600),
  ];
};

// Cache promotion card HTML to avoid re-fetching
let promotionCardHTML = '';
let wishlistGridRenderId = 0;
let wishlistButtonEventsInitialized = false;
const productBlockHTMLCache = new Map();
const productBlockRequestCache = new Map();

// ─── Lifecycle ────────────────────────────────────────────────────────────────

document.addEventListener('DOMContentLoaded', () => {
  initButtonEvents();
  initButtons();
  initGrid();
  updateWishlistBubble(getWishlist());
});

document.addEventListener('shopify-wishlist:updated', ({ detail }) => {
  console.log('[Shopify Wishlist] Wishlist Updated ✅', detail.wishlist);
  syncWishlistButtons(detail.wishlist);
  initGrid();
  updateWishlistBubble(detail.wishlist);
});

document.addEventListener('shopify-wishlist:init-product-grid', ({ detail }) => {
  console.log('[Shopify Wishlist] Product Grid Loaded ✅', detail.wishlist);
});

document.addEventListener('shopify-wishlist:init-buttons', ({ detail }) => {
  console.log('[Shopify Wishlist] Buttons Loaded ✅', detail.wishlist);
});

// ─── Grid ─────────────────────────────────────────────────────────────────────

const fetchproductBlockHTML = (handle) => {
  if (productBlockHTMLCache.has(handle)) {
    return Promise.resolve(productBlockHTMLCache.get(handle));
  }

  if (productBlockRequestCache.has(handle)) {
    return productBlockRequestCache.get(handle);
  }

  const request = (async () => {
    try {
      const res = await fetch(
        `/products/${encodeURIComponent(handle)}?view=product-block-template`,
        { cache: 'force-cache' }
      );

      if (!res.ok) {
        throw new Error(`Request failed with status ${res.status}`);
      }

      const text = await res.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');

      // Cache promotion card on first successful fetch
      if (!promotionCardHTML) {
        promotionCardHTML = doc.querySelector(selectors.promotionCard)?.outerHTML ?? '';
      }

      const productBlockHTML = doc.querySelector(selectors.productBlock)?.outerHTML ?? '';
      productBlockHTMLCache.set(handle, productBlockHTML);
      return productBlockHTML;
    } catch (err) {
      console.error(`[Shopify Wishlist] Failed to load card for: ${handle}`, err);
      return '';
    } finally {
      productBlockRequestCache.delete(handle);
    }
  })();

  productBlockRequestCache.set(handle, request);
  return request;
};

const setupGrid = async (grid) => {
  const renderId = ++wishlistGridRenderId;
  const wishlist = getWishlist();
  document.querySelector('.wish-info-wrapper')?.classList.toggle('hidden', wishlist.length > 0);
  grid.classList.toggle('is-loading', wishlist.length > 0);
  grid.setAttribute('aria-busy', String(wishlist.length > 0));

  const cards = await Promise.all(wishlist.map(fetchproductBlockHTML));

  if (renderId !== wishlistGridRenderId) return;

  const wishlistHTML = cards.filter(Boolean).join('');

  grid.innerHTML = wishlistHTML + promotionCardHTML;
  grid.classList.add(GRID_LOADED_CLASS);
  grid.classList.remove('is-loading');
  grid.setAttribute('aria-busy', 'false');
  initButtons();

  document.dispatchEvent(new CustomEvent('shopify-wishlist:init-product-grid', {
    detail: { wishlist },
  }));
};

const initGrid = () => {
  const grid = document.querySelector(selectors.grid);
  if (grid) setupGrid(grid);
};

// ─── Buttons ──────────────────────────────────────────────────────────────────

const syncWishlistButtons = (wishlist = getWishlist()) => {
  const wishlistHandles = new Set(wishlist);

  document.querySelectorAll(selectors.button).forEach((button) => {
    const handle = button.dataset.productHandle;
    const isActive = wishlistHandles.has(handle);
    button.classList.toggle(BUTTON_ACTIVE_CLASS, isActive);
    button.setAttribute('aria-pressed', String(isActive));
  });
};

const initButtonEvents = () => {
  if (wishlistButtonEventsInitialized) return;

  const preloadProductBlock = (event) => {
    if (!document.querySelector(selectors.grid)) return;

    const button = event.target.closest?.(selectors.button);
    const handle = button?.dataset.productHandle;
    if (handle) fetchproductBlockHTML(handle);
  };

  document.addEventListener('pointerover', preloadProductBlock, true);
  document.addEventListener('focusin', preloadProductBlock, true);

  document.addEventListener('click', (event) => {
    const button = event.target.closest(selectors.button);
    if (!button) return;

    event.preventDefault();
    event.stopPropagation();

    const handle = button.dataset.productHandle;
    if (!handle) {
      console.error('[Shopify Wishlist] Missing `data-product-handle` on button.');
      return;
    }

    updateWishlist(handle, button);
  }, true);

  wishlistButtonEventsInitialized = true;
};

const initButtons = () => {
  const buttons = document.querySelectorAll(selectors.button);
  if (!buttons.length) return;

  initButtonEvents();
  syncWishlistButtons();

  document.dispatchEvent(new CustomEvent('shopify-wishlist:init-buttons', {
    detail: { wishlist: getWishlist() },
  }));
};

// ─── Wishlist Storage ─────────────────────────────────────────────────────────

const getWishlist = () => {
  const stored = localStorage.getItem(LOCAL_STORAGE_WISHLIST_KEY);
  return stored ? stored.split(LOCAL_STORAGE_DELIMITER) : [];
};

const setWishlist = (array) => {
  if (array.length) {
    localStorage.setItem(LOCAL_STORAGE_WISHLIST_KEY, array.join(LOCAL_STORAGE_DELIMITER));
  } else {
    localStorage.removeItem(LOCAL_STORAGE_WISHLIST_KEY);
  }

  document.dispatchEvent(new CustomEvent('shopify-wishlist:updated', {
    detail: { wishlist: array },
  }));

  return array;
};

const updateWishlist = (handle, sourceButton) => {
  const wishlist = getWishlist();
  const idx = wishlist.indexOf(handle);
  const productId = sourceButton?.dataset.productId || '';
  const productTitle = sourceButton?.dataset.productTitle || '';

  if (idx === -1) {
    if (guestNeedsWishlistEmail()) {
      wishlist.push(handle);
      setWishlist(wishlist);
      openCartseeWishlistForm({
        handle,
        productId,
        productTitle,
      });
      return wishlist;
    }

    wishlist.push(handle);
    setWishlist(wishlist);
    // 收藏：从按钮取 product id
    sendCartseeEvent('wishlist_add', productId,productTitle);
  } else {
    if (pendingWishlistAdd?.handle === handle) {
      clearPendingWishlistAdd();
    }

    wishlist.splice(idx, 1);
    setWishlist(wishlist);
    // 取消收藏
    sendCartseeEvent('wishlist_remove', productId,productTitle);
  }

  return wishlist;
};

const wishlistContains = (handle) => getWishlist().includes(handle);

const resetWishlist = () => setWishlist([]);

// ─── Bubble Counter ───────────────────────────────────────────────────────────

const updateWishlistBubble = (wishlist) => {
  const count = wishlist.length;

  document.querySelectorAll('.wishlist-link__count').forEach((countEl) => {
    countEl.textContent = count > 0 ? count : '';
  });
};

// ─── Custom Elements ──────────────────────────────────────────────────────────

class PromotionWrapper extends HTMLElement {
  connectedCallback() {
    this.querySelector('svg')?.addEventListener('click', (e) => {
      this.classList.add('hidden');
      e.stopPropagation();
    });
  }
}
customElements.define('promotion-wrapper', PromotionWrapper);
